'use client';

import { formatAmount } from '@repo/ui/amount-text';
import { PaymentIcon } from '@repo/ui/payment-icon';
import { useTranslations } from 'next-intl';
import React, { memo, useEffect, useState } from 'react';
import MethodCard from '@/components/common/method-card';
import { MethodCardsSkeleton } from '@/components/common/skeletons/method-card-skeleton';
import { PAYMENT_TYPES } from '@/constants';
import useEnhancedPaymentMethods from '@/hooks/useEnhancedPaymentMethods';
import { type FullMethod } from '@/hooks/usePaymentMethodsWithNetworks';
import AmountPicker from './amount-picker';

const DepositFiat = () => {
  const t = useTranslations(PAYMENT_TYPES.DEPOSITS);

  const subtitleMap: Record<string, string> = {
    CPG: t('direct-bank-gateway'),
    C2C: t('card-to-card-gateway'),
    IPG: t('vip-gateway'),
  };

  const { isLoading, enhancedMethods } = useEnhancedPaymentMethods();

  const [selectedMethod, setSelectedMethod] = useState<FullMethod | null>(null);

  useEffect(() => {
    if (enhancedMethods.length === 1) {
      setSelectedMethod(enhancedMethods[0]);
    }
  }, [enhancedMethods]);

  if (isLoading) return <MethodCardsSkeleton count={3} />;

  if (selectedMethod) {
    return <AmountPicker selectedMethod={selectedMethod} />;
  }

  return (
    <div className="space-y-2 py-2 animate-in fade-in sm:space-y-4 sm:py-4">
      <div className="flex flex-col space-y-2 text-center vt-text-size-medium vt-headings-text">
        <span>{t('payment-methods-label')}</span>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {enhancedMethods.map((method) => {
          const { providerId, name, currencyCode, paymentGatewayMethod, minimumAmount = 0, maximumAmount = 0 } = method;

          const min = minimumAmount ? formatAmount(minimumAmount, currencyCode) : '';
          const max = maximumAmount ? formatAmount(maximumAmount, currencyCode) : '';
          const bottomText = min && max ? `${min} - ${max}` : min || max;

          return (
            <MethodCard
              key={`${providerId}_${paymentGatewayMethod}`}
              title={name.toLowerCase() === 'centralpay' ? subtitleMap[paymentGatewayMethod!] : name}
              subTitle={name.toLowerCase() !== 'centralpay' ? subtitleMap[paymentGatewayMethod!] : ''}
              iconRenderer={() => (
                <PaymentIcon
                  payment={name.toLowerCase()}
                  className="h-5 w-auto"
                />
              )}
              bottomContentRenderer={() => bottomText}
              onClick={() => setSelectedMethod(method)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default memo(DepositFiat);
